import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  User,
  Mail,
  Phone,
  Globe,
  Instagram,
  Facebook,
  Linkedin,
  Twitter,
  ArrowLeft,
  CheckCircle,
  Loader2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ReferralApplicationForm = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    instagram: "",
    facebook: "",
    linkedin: "",
    twitter: "",
    tiktok: "",
    website: "",
    why_interested: "",
    referral_strategy: "",
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    
    if (!formData.why_interested.trim()) {
      newErrors.why_interested = "Please tell us why you're interested";
    }
    
    if (!formData.referral_strategy.trim()) {
      newErrors.referral_strategy = "Please describe your referral strategy";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const params = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await fetch(`${API}/api/referrals/apply?${params.toString()}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Failed to submit application");
      }

      setIsSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to submit application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <>
        <Helmet>
          <title>Application Submitted | RNB Consulting Group</title>
        </Helmet>
        <Navbar />
        <main className="min-h-screen bg-gradient-subtle py-20">
          <div className="max-w-2xl mx-auto px-4">
            <Card className="card-modern text-center">
              <CardContent className="p-12">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="h-10 w-10 text-green-600" />
                </div>
                <h1 className="font-heading text-3xl font-bold text-rnb-navy mb-4">
                  Application Submitted!
                </h1>
                <p className="text-rnb-gray text-lg mb-6">
                  Thank you for your interest in joining our referral program. 
                  We&apos;ll review your application and get back to you within 2-3 business days.
                </p>
                <p className="text-sm text-rnb-gray mb-8">
                  Check your email for a confirmation of your application.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    onClick={() => navigate("/referral-program")}
                    variant="outline"
                    className="border-rnb-blue text-rnb-blue hover:bg-rnb-blue hover:text-white"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Program Info
                  </Button>
                  <Button
                    onClick={() => navigate("/")}
                    className="btn-primary"
                  >
                    Go to Homepage
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Referral Partner Application | RNB Consulting Group</title>
        <meta
          name="description"
          content="Complete your referral partner application. Share healthcare consulting resources with your network and earn commissions for successful referrals."
        />
      </Helmet>

      <Navbar />

      <main className="min-h-screen bg-gradient-subtle py-12">
        <div className="max-w-3xl mx-auto px-4">
          {/* Back Link */}
          <button
            onClick={() => navigate("/referral-program")}
            className="flex items-center text-rnb-blue hover:text-rnb-navy transition-colors mb-6"
            data-testid="back-to-referral-program"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Referral Program
          </button>

          <Card className="card-modern">
            <CardHeader className="text-center pb-2">
              <CardTitle className="font-heading text-3xl text-rnb-navy">
                Join Our Referral Program
              </CardTitle>
              <CardDescription className="text-lg">
                Fill out the form below to apply as a referral partner
              </CardDescription>
            </CardHeader>

            <CardContent className="p-6 sm:p-8">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Personal Information */}
                <div className="space-y-4">
                  <h3 className="font-heading text-xl font-semibold text-rnb-navy border-b pb-2">
                    Personal Information
                  </h3>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="flex items-center gap-2">
                        <User className="h-4 w-4 text-rnb-gray" />
                        Full Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="John Doe"
                        className={errors.name ? "border-red-500" : ""}
                        data-testid="referral-name-input"
                      />
                      {errors.name && (
                        <p className="text-sm text-red-500">{errors.name}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-rnb-gray" />
                        Email <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="john@example.com"
                        className={errors.email ? "border-red-500" : ""}
                        data-testid="referral-email-input"
                      />
                      {errors.email && (
                        <p className="text-sm text-red-500">{errors.email}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-rnb-gray" />
                      Phone Number (Optional)
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(555) 123-4567"
                      data-testid="referral-phone-input"
                    />
                  </div>
                </div>

                {/* Social Media & Web Presence */}
                <div className="space-y-4">
                  <h3 className="font-heading text-xl font-semibold text-rnb-navy border-b pb-2">
                    Social Media & Web Presence
                  </h3>
                  <p className="text-sm text-rnb-gray">
                    Share your social media profiles so we can learn more about your reach (all optional)
                  </p>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="instagram" className="flex items-center gap-2">
                        <Instagram className="h-4 w-4 text-rnb-gray" />
                        Instagram
                      </Label>
                      <Input
                        id="instagram"
                        name="instagram"
                        value={formData.instagram}
                        onChange={handleInputChange}
                        placeholder="@yourusername"
                        data-testid="referral-instagram-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="facebook" className="flex items-center gap-2">
                        <Facebook className="h-4 w-4 text-rnb-gray" />
                        Facebook
                      </Label>
                      <Input
                        id="facebook"
                        name="facebook"
                        value={formData.facebook}
                        onChange={handleInputChange}
                        placeholder="facebook.com/yourpage"
                        data-testid="referral-facebook-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="linkedin" className="flex items-center gap-2">
                        <Linkedin className="h-4 w-4 text-rnb-gray" />
                        LinkedIn
                      </Label>
                      <Input
                        id="linkedin"
                        name="linkedin"
                        value={formData.linkedin}
                        onChange={handleInputChange}
                        placeholder="linkedin.com/in/yourprofile"
                        data-testid="referral-linkedin-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="twitter" className="flex items-center gap-2">
                        <Twitter className="h-4 w-4 text-rnb-gray" />
                        Twitter/X
                      </Label>
                      <Input
                        id="twitter"
                        name="twitter"
                        value={formData.twitter}
                        onChange={handleInputChange}
                        placeholder="@yourusername"
                        data-testid="referral-twitter-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="tiktok" className="flex items-center gap-2">
                        <svg className="h-4 w-4 text-rnb-gray" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                        </svg>
                        TikTok
                      </Label>
                      <Input
                        id="tiktok"
                        name="tiktok"
                        value={formData.tiktok}
                        onChange={handleInputChange}
                        placeholder="@yourusername"
                        data-testid="referral-tiktok-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="website" className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-rnb-gray" />
                        Website
                      </Label>
                      <Input
                        id="website"
                        name="website"
                        value={formData.website}
                        onChange={handleInputChange}
                        placeholder="www.yourwebsite.com"
                        data-testid="referral-website-input"
                      />
                    </div>
                  </div>
                </div>

                {/* About You */}
                <div className="space-y-4">
                  <h3 className="font-heading text-xl font-semibold text-rnb-navy border-b pb-2">
                    About You
                  </h3>

                  <div className="space-y-2">
                    <Label htmlFor="why_interested">
                      Why are you interested in joining our referral program? <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="why_interested"
                      name="why_interested"
                      value={formData.why_interested}
                      onChange={handleInputChange}
                      placeholder="Tell us what draws you to our program and why you'd like to partner with us..."
                      rows={4}
                      className={errors.why_interested ? "border-red-500" : ""}
                      data-testid="referral-why-interested-input"
                    />
                    {errors.why_interested && (
                      <p className="text-sm text-red-500">{errors.why_interested}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="referral_strategy">
                      How do you plan to share our programs? <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="referral_strategy"
                      name="referral_strategy"
                      value={formData.referral_strategy}
                      onChange={handleInputChange}
                      placeholder="Describe your strategy for referring people to our programs (e.g., social media, networking events, professional connections, etc.)..."
                      rows={4}
                      className={errors.referral_strategy ? "border-red-500" : ""}
                      data-testid="referral-strategy-input"
                    />
                    {errors.referral_strategy && (
                      <p className="text-sm text-red-500">{errors.referral_strategy}</p>
                    )}
                  </div>
                </div>

                {/* Submit */}
                <div className="pt-4">
                  <div className="bg-rnb-light rounded-lg p-4 mb-6">
                    <p className="text-sm text-rnb-gray">
                      By submitting this application, you agree to our referral program terms. 
                      If approved, you&apos;ll receive login credentials and your unique referral code via email.
                    </p>
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full btn-primary py-6 text-lg"
                    disabled={isSubmitting}
                    data-testid="referral-submit-button"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      "Submit Application"
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </>
  );
};

export default ReferralApplicationForm;
